import React, { useEffect, useRef, useState } from "react";
import { MapPin, Pencil } from "lucide-react";
import LocationMapPicker from "./LocationMapPicker";
import { extractLocationDetails } from "./onboardingUtils";

// Compact field: shows a "Capture Location" button until a value is set, then the
// coordinates + resolved address with an edit affordance. Editing opens a modal with
// the actual map; changes only commit to the form on Save (Cancel discards the draft).
export default function LocationPicker({ latitude, longitude, disabled, onChange, onLocationDetails }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draft, setDraft] = useState({ latitude, longitude });
  const [address, setAddress] = useState("");
  const draftDetailsRef = useRef(null);
  // Coordinates the current `address` was already resolved for (via the map modal's search/
  // drag) — skips a redundant reverse-geocode below that could resolve to a different nearby
  // feature name than the one the user actually picked.
  const resolvedForRef = useRef({ lat: null, lng: null });

  const hasPosition = latitude !== "" && latitude !== null && longitude !== "" && longitude !== null;

  // Auto-capture the browser's current location once, the first time this field shows up
  // empty, so the user doesn't have to tap "Capture Location" for the common case.
  useEffect(() => {
    if (hasPosition || disabled || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => onChange(pos.coords.latitude, pos.coords.longitude),
      (error) => console.error("Auto-capture location failed:", error)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasPosition) {
      setAddress("");
      return;
    }
    if (resolvedForRef.current.lat === latitude && resolvedForRef.current.lng === longitude) return;
    let cancelled = false;
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setAddress(data?.display_name || "");
        onLocationDetails?.(extractLocationDetails(data?.address));
      })
      .catch((error) => console.error("Reverse geocode failed:", error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  const openModal = () => {
    if (disabled) return;
    setDraft({ latitude, longitude });
    draftDetailsRef.current = null;
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (draft.latitude !== "" && draft.longitude !== "") {
      resolvedForRef.current = { lat: draft.latitude, lng: draft.longitude };
      onChange(draft.latitude, draft.longitude);
      if (draftDetailsRef.current) onLocationDetails?.(draftDetailsRef.current);
    }
    setIsModalOpen(false);
  };

  return (
    <div>
      {hasPosition ? (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">
              {address || "Resolving address..."}
            </p>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={openModal}
              title="Edit location"
              className="shrink-0 rounded-full p-1.5 text-[#3cba84] hover:bg-[#eaf6f1] transition-colors"
            >
              <Pencil size={16} />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={openModal}
          disabled={disabled}
          className="w-full flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 hover:bg-gray-100 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        >
          <MapPin size={16} />
          Capture Location
        </button>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[88vh] flex flex-col">
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-base font-semibold text-[#004276]">Location</h3>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>
            <div className="px-6 pb-2 flex-1 min-h-0">
              <LocationMapPicker
                latitude={draft.latitude}
                longitude={draft.longitude}
                onChange={(lat, lng) => setDraft({ latitude: lat, longitude: lng })}
                onResolvedAddress={setAddress}
                onLocationDetails={(details) => (draftDetailsRef.current = details)}
              />
            </div>
            <div className="flex justify-end gap-3 px-6 py-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-5 py-2 bg-gradient-to-r from-[#004172] to-[#3cba84] text-white rounded-lg hover:opacity-90"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
